import { useCallback, useEffect, useRef, useState } from "react";
import { useApp } from "../../context/AppContext";
import { countdownLine, formatMediumDate } from "../../utils/calendar";
import { Avatar } from "../shared/Avatar";
import { EmptyState } from "../shared/LoadingState";
import { IconPlus } from "../shared/Icons";
import { ShellActionButton } from "../shared/ShellActionButton";

export function NewsView() {
  const {
    session,
    bundle,
    newsFeed,
    upcomingEvents,
    announcements,
    refreshAll,
    person,
    setSelectedPersonId,
    setShowAddAnnouncement,
    lastError,
  } = useApp();
  const [refreshing, setRefreshing] = useState(false);
  const [feedError, setFeedError] = useState<string | null>(null);
  const [pullPx, setPullPx] = useState(0);
  const startY = useRef<number | null>(null);
  const pullPxRef = useRef(0);
  const refreshingRef = useRef(false);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const onRefresh = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setRefreshing(true);
    setFeedError(null);
    try {
      await refreshAll();
    } catch (e) {
      setFeedError(e instanceof Error ? e.message : "Could not refresh feed.");
    } finally {
      refreshingRef.current = false;
      setRefreshing(false);
      pullPxRef.current = 0;
      setPullPx(0);
    }
  }, [refreshAll]);

  useEffect(() => {
    const el = scrollerRef.current?.closest(".screen") as HTMLElement | null;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      if (el.scrollTop <= 0) startY.current = e.touches[0]?.clientY ?? null;
      else startY.current = null;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (startY.current == null || refreshingRef.current) return;
      const y = e.touches[0]?.clientY ?? startY.current;
      const delta = Math.max(0, Math.min(88, y - startY.current));
      if (el.scrollTop <= 0 && delta > 0) {
        pullPxRef.current = delta;
        setPullPx(delta);
      }
    };
    const onTouchEnd = () => {
      if (pullPxRef.current > 56) void onRefresh();
      else {
        pullPxRef.current = 0;
        setPullPx(0);
      }
      startY.current = null;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [onRefresh]);

  if (!session || !bundle) return null;

  const birthdays = upcomingEvents.filter(
    (e) => e.kind === "birthday" || e.kind === "anniversary",
  );
  const approvedEvents = newsFeed?.approved_events ?? [];
  const pending = newsFeed?.pending_submissions ?? [];
  const bundledMoments = bundle.recent_events;

  return (
    <div className="pad-screen stack news-view" ref={scrollerRef}>
      <div
        className={`news-pull ${refreshing || pullPx > 8 ? "visible" : ""}`}
        style={{ height: refreshing ? 36 : pullPx * 0.55 }}
        aria-hidden
      >
        <span className={`news-pull-spinner ${refreshing ? "spin" : ""}`} />
      </div>

      {feedError || lastError ? (
        <p className="error-text">{feedError ?? lastError}</p>
      ) : null}

      <section>
        <h2 className="section-title">Upcoming birthdays</h2>
        {birthdays.length === 0 ? (
          <EmptyState message="No upcoming birthdays in the next few weeks." />
        ) : (
          <div className="stack">
            {birthdays.map((event) => {
              const primaryId = event.person_ids[0];
              const p = primaryId ? person(primaryId) : undefined;
              return (
                <button
                  key={event.id}
                  type="button"
                  className="birthday-row"
                  onClick={() => primaryId && setSelectedPersonId(primaryId)}
                >
                  {p ? <Avatar person={p} size={48} ring /> : null}
                  <div style={{ flex: 1, textAlign: "left" }}>
                    <strong>{event.label}</strong>
                    <div className="muted">
                      {formatMediumDate(event.occurs_on)} · {countdownLine(event.occurs_on)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <h2 className="section-title">Family events</h2>
        {approvedEvents.length === 0 && bundledMoments.length === 0 ? (
          <EmptyState message="No family events yet — tap + in the header to suggest one." />
        ) : (
          <div className="stack">
            {approvedEvents.map((event) => (
              <div key={event.id} className="card pad-screen">
                <strong>{event.title}</strong>
                <div className="muted">{formatMediumDate(event.occurs_on)}</div>
                {event.location_text ? <div className="muted">{event.location_text}</div> : null}
                {event.link_url ? (
                  <a href={event.link_url} target="_blank" rel="noreferrer">
                    Link
                  </a>
                ) : null}
              </div>
            ))}
            {bundledMoments.slice(0, 5).map((event) => (
              <div key={event.id} className="card pad-screen">
                <strong>{event.title}</strong>
                {event.subtitle ? <div className="muted">{event.subtitle}</div> : null}
              </div>
            ))}
          </div>
        )}
      </section>

      {pending.length > 0 ? (
        <section>
          <h2 className="section-title review">Pending review</h2>
          {pending.map((p) => (
            <div key={p.id} className="card pad-screen pending-card">
              {p.title ?? p.kind} · awaiting approval
            </div>
          ))}
        </section>
      ) : null}

      <section>
        <div className="section-heading-row">
          <h2 className="section-title" style={{ margin: 0 }}>
            Announcements
          </h2>
          <ShellActionButton
            label="Post an announcement"
            onClick={() => setShowAddAnnouncement(true)}
          >
            <IconPlus />
          </ShellActionButton>
        </div>
        {announcements.length === 0 ? (
          <EmptyState message="Engagements, new babies, milestones — be the first to share some news!" />
        ) : (
          <div className="stack">
            {announcements.map((a) => (
              <div key={a.id} className="card pad-screen">
                <strong>{a.display_name}</strong>
                <p>{a.message}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
